import { useState, useEffect } from 'react'
import { startThreadsGenerate, pollThreadsJob, startThreadsSuggestTopic } from '../utils/api'
import { C, font } from './ProfileResult'

function useIsMobile(bp = 640) {
  const [m, setM] = useState(window.innerWidth < bp)
  useEffect(() => {
    const h = () => setM(window.innerWidth < bp)
    window.addEventListener('resize', h)
    return () => window.removeEventListener('resize', h)
  }, [bp])
  return m
}

const glassPanel = {
  background: C.surface,
  border: `1px solid ${C.border}`,
  borderRadius: 12,
  padding: '20px 24px',
  marginBottom: 20,
}

const MOODS = [
  { id: 'Нейтрально',  emoji: '○' },
  { id: 'Размышление', emoji: '◌' },
  { id: 'Провокация',  emoji: '⚡' },
  { id: 'Ирония',      emoji: '↯' },
  { id: 'Вдохновение', emoji: '✦' },
  { id: 'Злость',      emoji: '△' },
  { id: 'Уязвимость',  emoji: '◎' },
]

const FORMATS = [
  { key: 'short',  label: 'Короткий', hint: '1-3 предложения · до 150 симв' },
  { key: 'medium', label: 'Средний',  hint: '4-8 предложений · 150-500 симв' },
  { key: 'long',   label: 'Длинный',  hint: 'С историей и выводом · 500-1200 симв' },
]

function Spinner({ size = 13, color = C.accent }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 0.9s linear infinite', flexShrink: 0 }}>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="2" strokeOpacity="0.25" />
      <path d="M12 3a9 9 0 0 1 9 9" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  )
}

function StageCard({ stage }) {
  const s = stage.status
  const isDone = s === 'done'
  const isRunning = s === 'running'
  const isRetrying = s === 'retrying'
  const isError = s === 'error'
  const active = isRunning || isRetrying
  return (
    <div style={{
      flex: 1, minWidth: 0, padding: '10px 6px', borderRadius: 8, textAlign: 'center',
      background: isDone ? 'rgba(77,200,138,0.08)' : isError ? 'rgba(212,90,90,0.08)' : active ? 'rgba(184,189,212,0.08)' : 'rgba(255,255,255,0.02)',
      border: `1px solid ${isDone ? 'rgba(77,200,138,0.3)' : isError ? 'rgba(212,90,90,0.3)' : active ? C.borderHi : C.border}`,
      transition: 'all 0.3s',
    }}>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 4, height: 14 }}>
        {isDone
          ? <span style={{ color: 'rgba(77,200,138,0.9)', fontSize: 13 }}>✓</span>
          : isError
          ? <span style={{ color: 'rgba(220,100,100,0.9)', fontSize: 13 }}>✕</span>
          : active
          ? <Spinner size={13} color={isRetrying ? '#d4a24a' : C.accent} />
          : <span style={{ color: C.textDim, fontSize: 10 }}>·</span>}
      </div>
      <div style={{
        fontFamily: font.mono, fontSize: 9,
        color: isDone ? 'rgba(77,200,138,0.9)' : isError ? 'rgba(220,100,100,0.9)' : active ? C.accent : C.textDim,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {stage.label}
      </div>
      {isRetrying && stage.retries > 0 && (
        <div style={{ fontFamily: font.mono, fontSize: 8, color: '#d4a24a', marginTop: 2 }}>↺ {stage.retries}</div>
      )}
    </div>
  )
}

function PipelineProgress({ stages, criticNote }) {
  if (!stages) return null
  const hasNote = criticNote && (criticNote.style_fit || criticNote.engagement_note)
  return (
    <div style={{ ...glassPanel, marginBottom: 20 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: hasNote ? 14 : 0 }}>
        {stages.map(s => <StageCard key={s.key} stage={s} />)}
      </div>
      {hasNote && (
        <div style={{ paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
          <div style={{ fontFamily: font.mono, fontSize: 9, letterSpacing: '0.1em', color: C.textDim, marginBottom: 6, textTransform: 'uppercase' }}>
            ◈ Заметка критика
          </div>
          {criticNote.style_fit && (
            <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, marginBottom: 4, lineHeight: 1.5 }}>
              {criticNote.style_fit}
            </div>
          )}
          {criticNote.engagement_note && (
            <div style={{ fontFamily: font.body, fontSize: 12, color: C.accent, lineHeight: 1.5 }}>
              → {criticNote.engagement_note}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function SuggestionList({ suggestions, onPick, onClose }) {
  return (
    <div style={glassPanel}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontFamily: font.mono, fontSize: 10, letterSpacing: '0.1em', color: C.textDim, textTransform: 'uppercase' }}>
          Темы из дневника
        </div>
        <button
          onClick={onClose}
          style={{ background: 'transparent', border: 'none', color: C.textDim, cursor: 'pointer', fontSize: 14, lineHeight: 1 }}
        >✕</button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {suggestions.map((s, i) => (
          <button
            key={i}
            onClick={() => onPick(s.seed || s.label)}
            style={{
              textAlign: 'left', padding: '10px 14px', borderRadius: 8,
              background: 'rgba(255,255,255,0.02)', border: `1px solid ${C.border}`,
              cursor: 'pointer', transition: 'all 0.15s',
            }}
          >
            <div style={{ fontFamily: font.body, fontSize: 13, color: C.accent, marginBottom: 3 }}>{s.label}</div>
            {s.seed && (
              <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, lineHeight: 1.5 }}>{s.seed}</div>
            )}
          </button>
        ))}
      </div>
    </div>
  )
}

function PostCard({ format, text, onChange, onCopy, copied }) {
  return (
    <div style={{
      ...glassPanel,
      marginBottom: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontFamily: font.mono, fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.accent, marginBottom: 2 }}>
            {format.label}
          </div>
          <div style={{ fontFamily: font.mono, fontSize: 9, color: C.textDim }}>
            {format.hint}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {text && (
            <span style={{ fontFamily: font.mono, fontSize: 9, color: C.textDim }}>
              {text.length} симв
            </span>
          )}
          <button
            onClick={onCopy}
            disabled={!text}
            style={{
              padding: '3px 10px', borderRadius: 6,
              fontFamily: font.mono, fontSize: 9,
              background: copied ? 'rgba(77,200,138,0.12)' : 'transparent',
              border: `1px solid ${copied ? 'rgba(77,200,138,0.4)' : C.border}`,
              color: copied ? 'rgba(77,200,138,0.9)' : C.textMuted,
              cursor: text ? 'pointer' : 'default',
              opacity: text ? 1 : 0.3,
              transition: 'all 0.2s',
            }}
          >{copied ? '✓' : '⎘'}</button>
        </div>
      </div>

      <textarea
        value={text || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={text === undefined ? '...' : ''}
        rows={format.key === 'short' ? 3 : format.key === 'medium' ? 6 : 10}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: '10px 12px', borderRadius: 8,
          fontFamily: font.body, fontSize: 13, lineHeight: 1.7,
          background: 'rgba(255,255,255,0.02)',
          border: `1px solid ${C.border}`,
          color: text ? C.text : C.textDim,
          outline: 'none', resize: 'vertical',
        }}
      />
    </div>
  )
}

export default function ThreadsEditor({ userId = 'user' }) {
  const isMobile = useIsMobile()
  const [topic, setTopic]             = useState('')
  const [mood, setMood]               = useState('Нейтрально')
  const [results, setResults]         = useState(null)   // {short, medium, long}
  const [comment, setComment]         = useState('')
  const [loading, setLoading]         = useState(false)
  const [pipeline, setPipeline]       = useState(null)    // {stages, criticNote}
  const [error, setError]             = useState(null)
  const [cost, setCost]               = useState(null)
  const [copied, setCopied]           = useState({})
  const [suggestions, setSuggestions] = useState(null)
  const [suggestLoading, setSuggestLoading] = useState(false)
  const [suggestError, setSuggestError]     = useState(null)

  const generate = async (withComment = false) => {
    if (!topic.trim()) return
    setLoading(true)
    setError(null)
    setPipeline(null)
    if (!withComment) setResults(null)

    try {
      const jobId = await startThreadsGenerate(userId, topic.trim(), mood, withComment ? comment.trim() : '')
      const result = await pollThreadsJob(jobId, (data) => {
        setPipeline({ stages: data.stages || [], criticNote: data.critic_note })
      })
      setResults({ short: result.short || '', medium: result.medium || '', long: result.long || '' })
      if (result.usage?.cost_usd) setCost(result.usage.cost_usd)
      if (withComment) setComment('')
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  const suggestTopic = async () => {
    setSuggestLoading(true)
    setSuggestError(null)
    setSuggestions(null)
    try {
      const jobId = await startThreadsSuggestTopic(userId)
      const result = await pollThreadsJob(jobId, () => {})
      const list = result.suggestions || []
      if (!list.length) throw new Error('Не удалось найти темы')
      setSuggestions(list)
    } catch (e) {
      setSuggestError(e.message)
    } finally {
      setSuggestLoading(false)
    }
  }

  const pickSuggestion = (seed) => {
    setTopic(seed)
    setSuggestions(null)
  }

  const copy = (key, text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(p => ({ ...p, [key]: true }))
      setTimeout(() => setCopied(p => ({ ...p, [key]: false })), 2000)
    })
  }

  const updateResult = (key, val) => setResults(p => ({ ...p, [key]: val }))

  return (
    <div style={{ paddingBottom: 60 }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: font.mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.textDim, marginBottom: 6 }}>
          Редактор текстов
        </div>
        <div style={{ fontFamily: font.heading, fontSize: 28, color: C.text, fontWeight: 400 }}>
          Threads в твоём стиле
        </div>
        <p style={{ fontFamily: font.body, fontSize: 13, color: C.textMuted, marginTop: 8, lineHeight: 1.6 }}>
          Напиши тему — получишь три варианта поста одновременно: короткий, средний, длинный.
        </p>
      </div>

      {/* Mood selector */}
      <div style={{ ...glassPanel }}>
        <div style={{ fontFamily: font.mono, fontSize: 10, letterSpacing: '0.1em', color: C.textDim, marginBottom: 12, textTransform: 'uppercase' }}>
          Настроение
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {MOODS.map(m => (
            <button
              key={m.id}
              onClick={() => setMood(m.id)}
              style={{
                padding: '6px 14px', borderRadius: 20,
                fontFamily: font.body, fontSize: 12,
                background: mood === m.id ? 'rgba(184,189,212,0.15)' : 'transparent',
                border: `1px solid ${mood === m.id ? C.borderHi : C.border}`,
                color: mood === m.id ? C.accent : C.textMuted,
                cursor: 'pointer', transition: 'all 0.15s',
              }}
            >
              {m.emoji} {m.id}
            </button>
          ))}
        </div>
      </div>

      {/* Topic */}
      <div style={glassPanel}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ fontFamily: font.mono, fontSize: 10, letterSpacing: '0.1em', color: C.textDim, textTransform: 'uppercase' }}>
            Текст поста
          </div>
          <button
            onClick={suggestTopic}
            disabled={suggestLoading}
            style={{
              padding: '4px 12px', borderRadius: 20,
              fontFamily: font.body, fontSize: 11,
              background: 'transparent',
              border: `1px solid ${C.border}`,
              color: suggestLoading ? C.textDim : C.accent,
              cursor: suggestLoading ? 'default' : 'pointer',
              transition: 'all 0.15s',
            }}
          >
            {suggestLoading ? '⏳ ищу темы...' : '💡 Предложи тему'}
          </button>
        </div>
        <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, marginBottom: 10, lineHeight: 1.5 }}>
          Изложи свою мысль как есть — конвейер агентов развернёт её в три формата в твоём стиле
        </div>
        <textarea
          value={topic}
          onChange={e => setTopic(e.target.value)}
          placeholder="Напиши что думаешь. Можно сыро, можно коротко — главное суть."
          rows={5}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '10px 14px', borderRadius: 8,
            fontFamily: font.body, fontSize: 14, lineHeight: 1.65,
            background: 'rgba(255,255,255,0.03)',
            border: `1px solid ${C.border}`,
            color: C.text, outline: 'none', resize: 'vertical',
          }}
        />
      </div>

      {/* Suggestions */}
      {suggestError && (
        <div style={{ padding: '10px 16px', borderRadius: 8, background: 'rgba(212,90,90,0.08)', border: '1px solid rgba(212,90,90,0.25)', color: 'rgba(220,100,100,0.9)', fontFamily: font.body, fontSize: 13, marginBottom: 20 }}>
          {suggestError}
        </div>
      )}
      {suggestions && (
        <SuggestionList suggestions={suggestions} onPick={pickSuggestion} onClose={() => setSuggestions(null)} />
      )}

      {/* Generate button */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 28 }}>
        <button
          onClick={() => generate(false)}
          disabled={loading || (!topic.trim())}
          style={{
            padding: '10px 28px', borderRadius: 8,
            fontFamily: font.body, fontSize: 13, fontWeight: 500,
            background: loading ? 'rgba(184,189,212,0.06)' : 'rgba(184,189,212,0.12)',
            border: `1px solid ${loading ? C.border : C.borderHi}`,
            color: loading ? C.textMuted : C.accent,
            cursor: loading || (!topic.trim()) ? 'default' : 'pointer',
            opacity: (!topic.trim()) ? 0.4 : 1,
            transition: 'all 0.2s',
          }}
        >
          {loading ? '⏳ Генерирую...' : '✦ Написать в моём стиле'}
        </button>
        {cost && !loading && (
          <span style={{ fontFamily: font.mono, fontSize: 10, color: C.textDim }}>${cost.toFixed(4)}</span>
        )}
      </div>

      {/* Pipeline progress */}
      {loading && <PipelineProgress stages={pipeline?.stages} criticNote={pipeline?.criticNote} />}
      {!loading && results && pipeline?.criticNote && (
        <PipelineProgress stages={pipeline?.stages} criticNote={pipeline?.criticNote} />
      )}

      {/* Error */}
      {error && (
        <div style={{ padding: '10px 16px', borderRadius: 8, background: 'rgba(212,90,90,0.08)', border: '1px solid rgba(212,90,90,0.25)', color: 'rgba(220,100,100,0.9)', fontFamily: font.body, fontSize: 13, marginBottom: 20 }}>
          {error}
        </div>
      )}

      {/* 3 cards */}
      {results && (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: 16,
            marginBottom: 20,
          }}>
            {FORMATS.map(fmt => (
              <PostCard
                key={fmt.key}
                format={fmt}
                text={results[fmt.key]}
                onChange={val => updateResult(fmt.key, val)}
                onCopy={() => copy(fmt.key, results[fmt.key])}
                copied={copied[fmt.key]}
              />
            ))}
          </div>

          {/* Comment + regenerate */}
          <div style={{ ...glassPanel, marginBottom: 0 }}>
            <div style={{ fontFamily: font.mono, fontSize: 10, letterSpacing: '0.1em', color: C.textDim, marginBottom: 10, textTransform: 'uppercase' }}>
              Правка — перегенерировать все три
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
              <input
                value={comment}
                onChange={e => setComment(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !loading && comment.trim() && generate(true)}
                placeholder="Сделай жёстче / убери пафос / добавь конкретику..."
                style={{
                  flex: 1,
                  padding: '9px 14px', borderRadius: 8,
                  fontFamily: font.body, fontSize: 13,
                  background: 'rgba(255,255,255,0.03)',
                  border: `1px solid ${C.border}`,
                  color: C.text, outline: 'none',
                }}
              />
              <button
                onClick={() => generate(true)}
                disabled={loading || !comment.trim()}
                style={{
                  padding: '9px 20px', borderRadius: 8,
                  fontFamily: font.mono, fontSize: 10,
                  background: 'transparent',
                  border: `1px solid ${comment.trim() && !loading ? C.borderHi : C.border}`,
                  color: comment.trim() && !loading ? C.accent : C.textMuted,
                  cursor: loading || !comment.trim() ? 'default' : 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s',
                }}
              >
                {loading ? '⏳' : '↻ перегенерировать'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
